import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getInitials } from '../../../lib/helpers/avatar';
import { IUser } from '../../../lib/interfaces';
import { getFormattedPersonalNumber } from '../../../lib/helpers/personalNumber';
import Brand from '../../common/Brand';
import { CallPhase } from '../types';
import { DS } from '../designTokens';
import { usePatientName } from '../hooks/usePatientName';

type Props = {
  patient: IUser;
  phase: CallPhase;
};

const VideoCallHeader = ({ patient, phase }: Props) => {
  const { t } = useTranslation();
  const patientName = usePatientName(patient);

  return (
    <Box
      component="header"
      sx={{
        height: 58,
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        px: '22px',
        bgcolor: DS.linen,
        color: DS.softBlack,
        borderBottom: `1px solid ${DS.lineSoft}`,
        fontFamily: DS.fontBody,
      }}
    >
      <Brand name={t('videoVisit.brand')} />
      <Box sx={{ width: '1px', height: 24, bgcolor: DS.lineStrong }} />
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            borderRadius: '50%',
            bgcolor: DS.surfaceTint,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 10.5,
          }}
        >
          {getInitials(patientName)}
        </Box>
        <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
          <Box sx={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            {patientName}
          </Box>
          {patient.personalNumber && (
            <Box sx={{ fontSize: 11, color: DS.muted, whiteSpace: 'nowrap' }}>
              {getFormattedPersonalNumber(patient.personalNumber)}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '14px' }}
      >
        {phase === 'connected' && (
          <Box
            sx={{
              px: '11px',
              py: '5px',
              borderRadius: '999px',
              bgcolor: DS.surfaceTint,
              color: DS.softBlack,
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {t('videoVisit.inProgress')}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoCallHeader;
